/*!
 * Clock In Cheeze — Season 1 gateway
 *
 * Opens the episode board the moment the 3D preloader starts fading, and
 * puts it back on demand from the corner dock — the social icons and the
 * sticky SEASON 1 pill. Markup, styling, social handles and per-episode
 * config all live in snippets/cic-season-gateway.liquid; this file only
 * handles when the board is on screen and how it is dismissed.
 *
 * Hand-off from the preloader is deliberately belt-and-braces: the
 * preloader dispatches `cic:preloader-hidden` as it starts its fade, but
 * it can also bail out early (no WebGL, missing image, already shown this
 * session) on paths that never reach that dispatch. So the DOM is polled
 * as well, and a hard timeout opens the board regardless.
 */
(function () {
  'use strict';

  var root = document.getElementById('cic-gateway');
  var fab = document.getElementById('cic-gateway-open');
  // The corner dock holds the socials and the re-open pill together, and
  // is what actually gets shown and hidden — the pill alone is never
  // toggled, or the socials would be left floating on their own.
  var dock = document.getElementById('cic-dock');
  if (!root || !fab || !dock) return;

  var panel = root.querySelector('.cic-gateway__panel');
  var skipBtn = root.querySelector('[data-gateway-skip]');
  var scrim = root.querySelector('[data-gateway-dismiss]');

  var AUTO_OPEN = root.getAttribute('data-auto-open') === 'true';
  var IS_HOME = root.getAttribute('data-is-home') === 'true';
  var HOME_URL = root.getAttribute('data-home-url') || '/';

  // Longest the board will wait on the preloader before showing itself.
  // Comfortably past the preloader's own 6s maxTime safety net.
  var HANDOFF_TIMEOUT_MS = 9000;
  var HANDOFF_POLL_MS = 150;
  // Let the preloader's 0.6s fade get underway so the two cross-fade
  // instead of stacking at full opacity.
  var HANDOFF_LEAD_MS = 220;
  var FADE_MS = 500;

  var isOpen = false;
  var closeTimer = null;
  var handoffDone = false;

  var FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function lockScroll(locked) {
    document.documentElement.style.overflow = locked ? 'hidden' : '';
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  // Both reveals go through a forced reflow rather than a rAF: rAF never
  // fires in a backgrounded tab, which would leave the overlay mounted at
  // opacity 0 — invisible, but still swallowing clicks and holding the
  // scroll lock — for anyone who opens the store in a background tab.
  function reveal(el, cls) {
    void el.offsetWidth; // flush layout so the transition has a start state
    el.classList.add(cls);
  }

  function showDock() {
    dock.hidden = false;
    reveal(dock, 'is-visible');
  }

  function hideDock() {
    dock.classList.remove('is-visible');
    dock.hidden = true;
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    hideDock();
    root.hidden = false;
    lockScroll(true);

    reveal(root, 'is-open');

    // Focus the panel itself rather than the first link, so a screen
    // reader announces the board before its options.
    if (panel) {
      panel.setAttribute('tabindex', '-1');
      panel.focus({ preventScroll: true });
    }

    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;

    root.classList.remove('is-open');
    lockScroll(false);
    document.removeEventListener('keydown', onKeydown);

    closeTimer = setTimeout(function () {
      root.hidden = true;
      closeTimer = null;
    }, FADE_MS);

    showDock();
    // Focus lands on the pill rather than wherever it was before: the
    // pill is visibly where the board just went, and it is the way back
    // in. Anything else strands keyboard focus behind a dismissed dialog.
    fab.focus({ preventScroll: true });
  }

  function skip() {
    if (IS_HOME) {
      close();
    } else {
      window.location.href = HOME_URL;
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;

    // Keep tabbing inside the board while it owns the screen.
    var items = Array.prototype.filter.call(
      root.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  fab.addEventListener('click', open);
  if (skipBtn) skipBtn.addEventListener('click', skip);
  if (scrim) scrim.addEventListener('click', close);

  if (!AUTO_OPEN) {
    showDock();
    return;
  }

  // ── Hand-off from the preloader ───────────────────────────────────
  function preloaderGone() {
    var pre = document.getElementById('cic-preloader');
    return !pre || pre.classList.contains('is-hidden');
  }

  function handoff() {
    if (handoffDone) return;
    handoffDone = true;
    document.removeEventListener('cic:preloader-hidden', handoff);
    clearInterval(pollId);
    clearTimeout(timeoutId);
    setTimeout(open, HANDOFF_LEAD_MS);
  }

  document.addEventListener('cic:preloader-hidden', handoff);
  var pollId = setInterval(function () {
    if (preloaderGone()) handoff();
  }, HANDOFF_POLL_MS);
  var timeoutId = setTimeout(handoff, HANDOFF_TIMEOUT_MS);

  // No preloader on screen at all (it only renders on the homepage, and
  // can remove itself before this script runs) — open straight away.
  if (preloaderGone()) handoff();
})();
